// ThemisRuleConditionsBuilder.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../../../components/Button/Button";
import TextField from "../../../components/FormFields/TextField";
import { useAlert } from "../../../components/Alert/Alerts";
import Icon from "../../../components/Icon/Icon";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";

import {
	createConditionGroup,
	deleteConditionGroup,
	updateConditionGroup,
	getReachableCatalog,
	createCondition,
	deleteCondition,
	updateCondition,
	ThemisEnumOptionDto,
	ThemisCatalogFieldSearchResultRowDto,
} from "./manageThemisRuleConditions";
import { ThemisPickFieldFn } from "./themisFieldPickerTypes";
import ThemisFieldPickerButton from "./ThemisFieldPickerButton";

import {
	ThemisConditionOperator,
	ThemisConditionValueType,
} from "./ThemisInterfaces";

type ConditionDraft = {
	stringValue?: string;
	boolValue?: boolean;
	intValue?: string; // keep as string while typing
	longValue?: string; // keep as string while typing
	decimalValue?: string; // keep as string while typing
	longValue2?: string;
	decimalValue2?: string;
	stringListText?: string;
	numberListText?: string;
	enumListValues?: string[];
};

type ConditionFieldMeta = {
	valueType: ThemisConditionValueType;
	allowedOperators: ThemisConditionOperator[];
	enumOptions: ThemisEnumOptionDto[];
};

type ThemisConditionDto = {
	id: number;
	themisConditionGroupId: number;
	fieldKey: string;
	operator: number; // backend enum int
	valueType: number; // backend enum int
	sortOrder: number;

	stringValue?: string | null;
	boolValue?: boolean | null;
	longValue?: number | null;
	intValue?: number | null;
	decimalValue?: number | null;
	longValue2?: number | null;
	decimalValue2?: number | null;
	stringListJson?: string | null;
	numberListJson?: string | null;
};

type ThemisConditionGroupDto = {
	id: number;
	themisRuleId: number;
	name: string;
	sortOrder: number;
	conditions: ThemisConditionDto[];
};

type Props = {
	ruleId: number;
	phaseKey: string;
	targetScopeKey: string;
	groups: ThemisConditionGroupDto[];
	pickField: ThemisPickFieldFn;
	onReload(): Promise<void>;
};

const intToOperator = (n: number): ThemisConditionOperator => {
	switch (n) {
		case 0:
			return ThemisConditionOperator.Equals;
		case 1:
			return ThemisConditionOperator.NotEquals;
		case 2:
			return ThemisConditionOperator.In;
		case 3:
			return ThemisConditionOperator.NotIn;
		case 4:
			return ThemisConditionOperator.GreaterThan;
		case 5:
			return ThemisConditionOperator.GreaterThanOrEqual;
		case 6:
			return ThemisConditionOperator.LessThan;
		case 7:
			return ThemisConditionOperator.LessThanOrEqual;
		case 8:
			return ThemisConditionOperator.Between;
		case 9:
			return ThemisConditionOperator.Contains;
		case 10:
			return ThemisConditionOperator.StartsWith;
		case 11:
			return ThemisConditionOperator.EndsWith;
		case 12:
			return ThemisConditionOperator.IsNull;
		case 13:
			return ThemisConditionOperator.IsNotNull;
		default:
			return ThemisConditionOperator.Equals;
	}
};

const operatorToInt = (op: ThemisConditionOperator): number => {
	switch (op) {
		case ThemisConditionOperator.Equals:
			return 0;
		case ThemisConditionOperator.NotEquals:
			return 1;
		case ThemisConditionOperator.In:
			return 2;
		case ThemisConditionOperator.NotIn:
			return 3;
		case ThemisConditionOperator.GreaterThan:
			return 4;
		case ThemisConditionOperator.GreaterThanOrEqual:
			return 5;
		case ThemisConditionOperator.LessThan:
			return 6;
		case ThemisConditionOperator.LessThanOrEqual:
			return 7;
		case ThemisConditionOperator.Between:
			return 8;
		case ThemisConditionOperator.Contains:
			return 9;
		case ThemisConditionOperator.StartsWith:
			return 10;
		case ThemisConditionOperator.EndsWith:
			return 11;
		case ThemisConditionOperator.IsNull:
			return 12;
		case ThemisConditionOperator.IsNotNull:
			return 13;
		default:
			return 0;
	}
};

const intToValueType = (n: number): ThemisConditionValueType => {
	switch (n) {
		case 0:
			return ThemisConditionValueType.String;
		case 1:
			return ThemisConditionValueType.Number;
		case 2:
			return ThemisConditionValueType.Boolean;
		case 3:
			return ThemisConditionValueType.DateTimeUtcMs;
		case 4:
			return ThemisConditionValueType.Int;
		case 5:
			return ThemisConditionValueType.Decimal;
		default:
			return ThemisConditionValueType.String;
	}
};

const valueTypeToInt = (vt: ThemisConditionValueType): number => {
	switch (vt) {
		case ThemisConditionValueType.String:
			return 0;
		case ThemisConditionValueType.Number:
			return 1;
		case ThemisConditionValueType.Boolean:
			return 2;
		case ThemisConditionValueType.DateTimeUtcMs:
			return 3;
		case ThemisConditionValueType.Int:
			return 4;
		case ThemisConditionValueType.Decimal:
			return 5;
		default:
			return 0;
	}
};

const normalizeValueType = (raw: unknown): ThemisConditionValueType => {
	if (typeof raw === "number") return intToValueType(raw);
	if (
		typeof raw === "string" &&
		Object.values(ThemisConditionValueType).includes(
			raw as ThemisConditionValueType,
		)
	) {
		return raw as ThemisConditionValueType;
	}
	return ThemisConditionValueType.String;
};

const normalizeOperator = (raw: unknown): ThemisConditionOperator | null => {
	if (typeof raw === "number") return intToOperator(raw);
	if (
		typeof raw === "string" &&
		Object.values(ThemisConditionOperator).includes(
			raw as ThemisConditionOperator,
		)
	) {
		return raw as ThemisConditionOperator;
	}
	return null;
};

const defaultOperatorsByValueType = (
	vt: ThemisConditionValueType,
): ThemisConditionOperator[] => {
	switch (vt) {
		case ThemisConditionValueType.Boolean:
			return [
				ThemisConditionOperator.Equals,
				ThemisConditionOperator.NotEquals,
				ThemisConditionOperator.IsNull,
				ThemisConditionOperator.IsNotNull,
			];
		case ThemisConditionValueType.String:
			return [
				ThemisConditionOperator.Equals,
				ThemisConditionOperator.NotEquals,
				ThemisConditionOperator.In,
				ThemisConditionOperator.NotIn,
				ThemisConditionOperator.Contains,
				ThemisConditionOperator.StartsWith,
				ThemisConditionOperator.EndsWith,
				ThemisConditionOperator.IsNull,
				ThemisConditionOperator.IsNotNull,
			];
		case ThemisConditionValueType.DateTimeUtcMs:
			return [
				ThemisConditionOperator.Equals,
				ThemisConditionOperator.NotEquals,
				ThemisConditionOperator.GreaterThan,
				ThemisConditionOperator.GreaterThanOrEqual,
				ThemisConditionOperator.LessThan,
				ThemisConditionOperator.LessThanOrEqual,
				ThemisConditionOperator.Between,
				ThemisConditionOperator.IsNull,
				ThemisConditionOperator.IsNotNull,
			];
		case ThemisConditionValueType.Int:
		case ThemisConditionValueType.Decimal:
		case ThemisConditionValueType.Number:
			return [
				ThemisConditionOperator.Equals,
				ThemisConditionOperator.NotEquals,
				ThemisConditionOperator.In,
				ThemisConditionOperator.NotIn,
				ThemisConditionOperator.GreaterThan,
				ThemisConditionOperator.GreaterThanOrEqual,
				ThemisConditionOperator.LessThan,
				ThemisConditionOperator.LessThanOrEqual,
				ThemisConditionOperator.Between,
				ThemisConditionOperator.IsNull,
				ThemisConditionOperator.IsNotNull,
			];
		default:
			return [ThemisConditionOperator.Equals];
	}
};

const noValueOperators = new Set<ThemisConditionOperator>([
	ThemisConditionOperator.IsNull,
	ThemisConditionOperator.IsNotNull,
]);

const listOperators = new Set<ThemisConditionOperator>([
	ThemisConditionOperator.In,
	ThemisConditionOperator.NotIn,
]);

const betweenOperators = new Set<ThemisConditionOperator>([
	ThemisConditionOperator.Between,
]);

const enumFriendlyOperators = new Set<ThemisConditionOperator>([
	ThemisConditionOperator.Equals,
	ThemisConditionOperator.NotEquals,
	ThemisConditionOperator.In,
	ThemisConditionOperator.NotIn,
	ThemisConditionOperator.IsNull,
	ThemisConditionOperator.IsNotNull,
]);

const Wrap = styled.section`
	width: 100%;
	padding: 1.5rem;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	box-shadow: 0 2px 10px 0 ${({ theme }) => theme.colorBoxShadow};
`;

const PanelTitle = styled.h3`
	margin: 0 0 0.25rem 0;
`;

const Muted = styled.p`
	font-size: 0.9rem;
	opacity: 0.7;
	margin: 0;
	max-width: 44rem;
`;

const HeaderBar = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	flex-wrap: wrap;
`;

const CreatePanel = styled.div`
	display: grid;
	grid-template-columns: minmax(220px, 280px) auto;
	gap: 0.75rem;
	align-items: end;

	@media only screen and (max-width: 720px) {
		width: 100%;
		grid-template-columns: 1fr;
	}
`;

const GroupCard = styled.div`
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
`;

const GroupHeader = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 1rem;
	align-items: end;
	padding: 1rem;
	border-bottom: 1px solid ${({ theme }) => theme.colorBorderLight};

	@media only screen and (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const GroupHeaderCopy = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.45rem;
`;

const Eyebrow = styled.div`
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	opacity: 0.58;
`;

const GroupHeaderActions = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 160px) 42px;
	align-items: end;
	gap: 0.5rem;
	justify-content: end;
	min-width: 0;
`;

const GroupSummary = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	font-size: 0.8rem;
	opacity: 0.78;
`;

const MetaPill = styled.span`
	display: inline-flex;
	align-items: center;
	min-height: 1.6rem;
	padding: 0.15rem 0.55rem;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: transparent;
	font-size: 0.74rem;
	font-weight: 600;
`;

const GroupBody = styled.div`
	padding: 0.9rem 1rem 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const ConditionRow = styled.div`
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	padding: 0.9rem;
	display: grid;
	grid-template-columns: minmax(0, 1.15fr) minmax(140px, 0.6fr) minmax(0, 1.35fr) 42px;
	gap: 0.9rem 1rem;
	align-items: end;
	min-width: 0;

	@media only screen and (max-width: 1320px) {
		grid-template-columns: 1fr;
	}
`;

const FieldStack = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.45rem;
`;

const FieldLabel = styled.label`
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	opacity: 0.62;
`;

const SelectField = styled.select`
	width: 100%;
	min-height: 50px;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	padding: 0 0.75rem;
	box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
`;

const InlineHint = styled.div`
	min-height: 50px;
	display: flex;
	align-items: center;
	padding: 0 0.75rem;
	border: 1px dashed ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	font-size: 0.88rem;
	opacity: 0.76;
`;

const IconButton = styled.button`
	width: 42px;
	height: 42px;
	min-width: 42px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
	transition:
		border-color 140ms ease-in,
		background 140ms ease-in;

	&:hover {
		border-color: ${({ theme }) => theme.colorActivation};
		background: ${({ theme }) => theme.colorBackgroundLightLight};
	}

	align-self: end;

	@media only screen and (max-width: 1180px) {
		justify-self: end;
	}
`;

export default function ThemisRuleConditionsBuilder({
	ruleId,
	phaseKey,
	targetScopeKey,
	groups,
	pickField,
	onReload,
}: Props) {
	const { addNewAlert } = useAlert();

	// Set group name
	const [newGroupName, setNewGroupName] = useState("");

	const [groupNameDrafts, setGroupNameDrafts] = useState<
		Record<number, string>
	>({});

	const [conditionDrafts, setConditionDrafts] = useState<
		Record<number, ConditionDraft>
	>({});
	const [activePickerConditionId, setActivePickerConditionId] = useState<
		number | null
	>(null);
	const [fieldMetaByKey, setFieldMetaByKey] = useState<
		Record<string, ConditionFieldMeta>
	>({});

	const isInt = (s: string) => /^-?\d*$/.test(s);
	const isLong = (s: string) => /^-?\d*$/.test(s); // same input rule, different backend field
	const isDecimal = (s: string) => /^-?\d*(\.\d*)?$/.test(s);

	const toNullableInt = (s: string) =>
		s.trim() === "" ? null : Number.parseInt(s, 10);
	const toNullableLong = (s: string) =>
		s.trim() === "" ? null : Number.parseInt(s, 10);
	const toNullableDecimal = (s: string) =>
		s.trim() === "" ? null : Number.parseFloat(s);

	const groupNameTimers = useRef<Record<number, NodeJS.Timeout>>({});

	const jsonArrayToCsv = (json: string | null | undefined): string => {
		if (!json) return "";
		try {
			const arr = JSON.parse(json);
			if (!Array.isArray(arr)) return "";
			return arr.map((x) => String(x)).join(", ");
		} catch {
			return "";
		}
	};

	const parseCsvStrings = (csv: string): string[] =>
		csv
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s.length > 0);

	const parseCsvNumbers = (csv: string): number[] =>
		csv
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s.length > 0)
			.map((s) => Number(s))
			.filter((n) => Number.isFinite(n));

	useEffect(() => {
		const next: Record<number, ConditionDraft> = {};
		groups.forEach((g) => {
			(g.conditions ?? []).forEach((c) => {
				next[c.id] = {
					stringValue: c.stringValue ?? "",
					boolValue: c.boolValue ?? false,
					intValue:
						c.intValue != null
							? String(c.intValue)
							: c.longValue != null
								? String(c.longValue)
								: "",
					longValue: c.longValue != null ? String(c.longValue) : "",
					longValue2: c.longValue2 != null ? String(c.longValue2) : "",
					decimalValue: c.decimalValue != null ? String(c.decimalValue) : "",
					decimalValue2: c.decimalValue2 != null ? String(c.decimalValue2) : "",
					stringListText: jsonArrayToCsv(c.stringListJson),
					numberListText: jsonArrayToCsv(c.numberListJson),
					enumListValues: c.numberListJson
						? parseCsvNumbers(jsonArrayToCsv(c.numberListJson)).map((n) =>
								String(Math.trunc(n)),
							)
						: [],
				};
			});
		});
		setConditionDrafts(next);
	}, [groups]);

	useEffect(() => {
		const next: Record<number, string> = {};
		groups.forEach((g) => (next[g.id] = g.name ?? ""));
		setGroupNameDrafts(next);
	}, [groups]);

	const debouncedSaveGroupName = (
		g: ThemisConditionGroupDto,
		value: string,
	) => {
		// Clear previous timer if user keeps typing
		if (groupNameTimers.current[g.id]) {
			clearTimeout(groupNameTimers.current[g.id]);
		}

		groupNameTimers.current[g.id] = setTimeout(async () => {
			const draft = value.trim();
			const current = (g.name ?? "").trim();

			if (!draft || draft === current) return;

			try {
				await updateConditionGroup(g.id, {
					name: draft,
					sortOrder: g.sortOrder,
				});

				addNewAlert({ type: "success", message: "Group updated." });
				await onReload();
			} catch {
				addNewAlert({ type: "error", message: "Failed to update group." });
			} finally {
				delete groupNameTimers.current[g.id];
			}
		}, 600); // ~0.6s pause before saving
	};

	const saveGroupName = async (g: ThemisConditionGroupDto) => {
		if (groupNameTimers.current[g.id]) {
			clearTimeout(groupNameTimers.current[g.id]);
			delete groupNameTimers.current[g.id];
		}

		const draft = (groupNameDrafts[g.id] ?? "").trim();
		const current = (g.name ?? "").trim();

		if (!draft || draft === current) return;

		try {
			await updateConditionGroup(g.id, { name: draft, sortOrder: g.sortOrder });
			addNewAlert({ type: "success", message: "Group updated." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to update group." });
			// optional: revert
			setGroupNameDrafts((prev) => ({ ...prev, [g.id]: g.name ?? "" }));
		}
	};

	const getAllowedOperatorsForCondition = (
		c: ThemisConditionDto,
	): ThemisConditionOperator[] => {
		const meta = fieldMetaByKey[c.fieldKey];
		const vt = meta?.valueType ?? intToValueType(c.valueType);
		const rawOps =
			meta?.allowedOperators && meta.allowedOperators.length > 0
				? meta.allowedOperators
				: defaultOperatorsByValueType(vt);
		const isEnum = (meta?.enumOptions?.length ?? 0) > 0;
		const filtered = isEnum
			? rawOps.filter((op) => enumFriendlyOperators.has(op))
			: rawOps;
		return filtered.length > 0 ? filtered : rawOps;
	};

	const clearValuePatch = (): Partial<ThemisConditionDto> => ({
		stringValue: null,
		boolValue: null,
		longValue: null,
		intValue: null,
		decimalValue: null,
		longValue2: null,
		decimalValue2: null,
		stringListJson: null,
		numberListJson: null,
	});

	const buildValuePatch = (
		c: ThemisConditionDto,
		op: ThemisConditionOperator,
	): Partial<ThemisConditionDto> => {
		const draft = conditionDrafts[c.id] ?? {};
		const meta = fieldMetaByKey[c.fieldKey];
		const vt = meta?.valueType ?? intToValueType(c.valueType);
		const isEnum = (meta?.enumOptions?.length ?? 0) > 0;
		const patch: Partial<ThemisConditionDto> = clearValuePatch();

		if (noValueOperators.has(op)) return patch;

		if (listOperators.has(op)) {
			if (isEnum) {
				const enumValues = (draft.enumListValues ?? [])
					.map((v) => Number.parseInt(v, 10))
					.filter((n) => Number.isFinite(n));
				patch.numberListJson = JSON.stringify(enumValues);
				return patch;
			}

			if (vt === ThemisConditionValueType.String) {
				patch.stringListJson = JSON.stringify(
					parseCsvStrings(draft.stringListText ?? ""),
				);
				return patch;
			}

			patch.numberListJson = JSON.stringify(
				parseCsvNumbers(draft.numberListText ?? ""),
			);
			return patch;
		}

		if (betweenOperators.has(op)) {
			if (vt === ThemisConditionValueType.Decimal) {
				patch.decimalValue = toNullableDecimal(draft.decimalValue ?? "");
				patch.decimalValue2 = toNullableDecimal(draft.decimalValue2 ?? "");
				return patch;
			}

			patch.longValue = toNullableLong(draft.longValue ?? "");
			patch.longValue2 = toNullableLong(draft.longValue2 ?? "");
			return patch;
		}

		if (vt === ThemisConditionValueType.String) {
			patch.stringValue = draft.stringValue ?? "";
			return patch;
		}

		if (vt === ThemisConditionValueType.Boolean) {
			patch.boolValue = draft.boolValue ?? false;
			return patch;
		}

		if (vt === ThemisConditionValueType.Decimal) {
			patch.decimalValue = toNullableDecimal(draft.decimalValue ?? "");
			return patch;
		}

		if (vt === ThemisConditionValueType.Int) {
			patch.intValue = toNullableInt(draft.intValue ?? "");
			return patch;
		}

		// Number + DateTimeUtcMs are long values in condition payload.
		patch.longValue = toNullableLong(draft.longValue ?? "");
		return patch;
	};

	useEffect(() => {
		if (!phaseKey || !targetScopeKey) return;

		getReachableCatalog({
			rootScopeKey: targetScopeKey,
			phaseKey,
		})
			.then((res) => {
				const scopes = res.data?.scopes ?? [];

				const nextMeta: Record<string, ConditionFieldMeta> = {};
				scopes.forEach((scope) => {
					(scope.fields ?? []).forEach((field) => {
						const normalizedOperators = (field.allowedOperators ?? [])
							.map((x) => normalizeOperator(x))
							.filter((x): x is ThemisConditionOperator => x !== null);
						const valueType = normalizeValueType(field.valueType);
						nextMeta[field.key] = {
							valueType,
							allowedOperators:
								normalizedOperators.length > 0
									? normalizedOperators
									: defaultOperatorsByValueType(valueType),
							enumOptions: field.enumOptions ?? [],
						};
					});
				});
				setFieldMetaByKey(nextMeta);
			})
			.catch(() => {
				setFieldMetaByKey({});
			});
	}, [phaseKey, targetScopeKey]);

	// ----------------------------
	// Groups helpers
	// ----------------------------
	const sortedGroups = useMemo(() => {
		return groups
			.slice()
			.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
	}, [groups]);

	const maxGroupSort = useMemo(() => {
		return sortedGroups.reduce((acc, g) => Math.max(acc, g.sortOrder ?? 0), 0);
	}, [sortedGroups]);

	const createGroup = async () => {
		const name = newGroupName.trim() || `Group ${sortedGroups.length + 1}`;

		try {
			await createConditionGroup(ruleId, {
				name,
				sortOrder: maxGroupSort + 10,
			});
			addNewAlert({ type: "success", message: "Condition group created." });
			setNewGroupName("");
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to create group." });
		}
	};

	const removeGroup = async (groupId: number) => {
		try {
			await deleteConditionGroup(groupId);
			addNewAlert({ type: "success", message: "Condition group deleted." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to delete group." });
		}
	};

	// ----------------------------
	// Add condition
	// ----------------------------
	const addConditionToGroup = async (
		field: ThemisCatalogFieldSearchResultRowDto,
		targetGroupId: number,
	) => {
		const group = groups.find((g) => g.id === targetGroupId);
		const maxSort = (group?.conditions ?? []).reduce(
			(acc, c) => Math.max(acc, c.sortOrder ?? 0),
			0,
		);

		try {
			const vt = normalizeValueType(field.valueType);
			const isEnum = (field.enumOptions?.length ?? 0) > 0;
			const enumDefault =
				isEnum && field.enumOptions && field.enumOptions.length > 0
					? Math.trunc(field.enumOptions[0].value)
					: null;

			await createCondition(targetGroupId, {
				fieldKey: field.fieldKey,
				operator: ThemisConditionOperator.Equals,
				valueType: vt,
				sortOrder: maxSort + 10,

				stringValue: vt === ThemisConditionValueType.String ? "" : null,
				boolValue: vt === ThemisConditionValueType.Boolean ? false : null,
				intValue:
					vt === ThemisConditionValueType.Int ? (enumDefault ?? 0) : null,
				longValue:
					vt === ThemisConditionValueType.Number ||
					vt === ThemisConditionValueType.DateTimeUtcMs
						? 0
						: null,
				decimalValue: vt === ThemisConditionValueType.Decimal ? 0 : null,

				longValue2: null,
				decimalValue2: null,
				stringListJson: null,
				numberListJson: null,
			});

			addNewAlert({ type: "success", message: "Condition added." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to add condition." });
		}
	};

	const buildInitialConditionPatch = (
		field: ThemisCatalogFieldSearchResultRowDto,
	): Partial<ThemisConditionDto> => {
		const vt = normalizeValueType(field.valueType);
		const isEnum = (field.enumOptions?.length ?? 0) > 0;
		const enumDefault =
			isEnum && field.enumOptions && field.enumOptions.length > 0
				? Math.trunc(field.enumOptions[0].value)
				: null;

		return {
			fieldKey: field.fieldKey,
			operator: operatorToInt(ThemisConditionOperator.Equals),
			valueType: valueTypeToInt(vt),
			stringValue: vt === ThemisConditionValueType.String ? "" : null,
			boolValue: vt === ThemisConditionValueType.Boolean ? false : null,
			intValue: vt === ThemisConditionValueType.Int ? (enumDefault ?? 0) : null,
			longValue:
				vt === ThemisConditionValueType.Number ||
				vt === ThemisConditionValueType.DateTimeUtcMs
					? 0
					: null,
			decimalValue: vt === ThemisConditionValueType.Decimal ? 0 : null,
			longValue2: null,
			decimalValue2: null,
			stringListJson: null,
			numberListJson: null,
		};
	};

	const handleAddConditionClick = async (group: ThemisConditionGroupDto) => {
		const field = await pickField({
			title: `Add Condition - ${group.name || `Group ${group.id}`}`,
		});
		if (!field) return;
		await addConditionToGroup(field, group.id);
	};

	// ----------------------------
	// Update condition value/editor (simple version)
	// ----------------------------
	const setConditionValue = async (
		c: ThemisConditionDto,
		patch: Partial<ThemisConditionDto>,
	) => {
		const patchHas = <K extends keyof ThemisConditionDto>(key: K) =>
			Object.prototype.hasOwnProperty.call(patch, key);

		try {
			await updateCondition(c.id, {
				fieldKey: patchHas("fieldKey") ? patch.fieldKey : c.fieldKey,
				operator:
					patchHas("operator") && patch.operator != null
						? intToOperator(patch.operator)
						: intToOperator(c.operator),
				valueType:
					patchHas("valueType") && patch.valueType != null
						? intToValueType(patch.valueType)
						: intToValueType(c.valueType),
				sortOrder: patch.sortOrder ?? c.sortOrder,

				stringValue: patchHas("stringValue")
					? (patch.stringValue ?? null)
					: (c.stringValue ?? null),
				boolValue: patchHas("boolValue")
					? (patch.boolValue ?? null)
					: (c.boolValue ?? null),
				longValue: patchHas("longValue")
					? (patch.longValue ?? null)
					: (c.longValue ?? null),
				intValue: patchHas("intValue")
					? (patch.intValue ?? null)
					: (c.intValue ?? null),
				decimalValue: patchHas("decimalValue")
					? (patch.decimalValue ?? null)
					: (c.decimalValue ?? null),
				longValue2: patchHas("longValue2")
					? (patch.longValue2 ?? null)
					: (c.longValue2 ?? null),
				decimalValue2: patchHas("decimalValue2")
					? (patch.decimalValue2 ?? null)
					: (c.decimalValue2 ?? null),
				stringListJson: patchHas("stringListJson")
					? (patch.stringListJson ?? null)
					: (c.stringListJson ?? null),
				numberListJson: patchHas("numberListJson")
					? (patch.numberListJson ?? null)
					: (c.numberListJson ?? null),
			});
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to update condition." });
		}
	};

	const pickFieldForCondition = async (c: ThemisConditionDto) => {
		setActivePickerConditionId(c.id);
		try {
			const field = await pickField({
				title: "Select Field Key",
			});
			if (!field) return;
			await setConditionValue(c, buildInitialConditionPatch(field));
		} finally {
			setActivePickerConditionId((current) =>
				current === c.id ? null : current,
			);
		}
	};

	const removeCondition = async (conditionId: number) => {
		try {
			await deleteCondition(conditionId);
			addNewAlert({ type: "success", message: "Condition deleted." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to delete condition." });
		}
	};

	const getValueLabel = (
		operator: ThemisConditionOperator,
		valueType: ThemisConditionValueType,
		isEnumField: boolean,
	) => {
		if (noValueOperators.has(operator)) return "Value";
		if (listOperators.has(operator)) {
			return isEnumField
				? "Values"
				: valueType === ThemisConditionValueType.String
					? "Values (comma separated)"
					: "Numbers (comma separated)";
		}
		if (betweenOperators.has(operator)) {
			return valueType === ThemisConditionValueType.DateTimeUtcMs
				? "Range (UTC ms)"
				: "Range";
		}
		return "Value";
	};

	const renderConditionValueEditor = (
		c: ThemisConditionDto,
		draft: ConditionDraft,
		vt: ThemisConditionValueType,
		enumOptions: ThemisEnumOptionDto[],
		isEnumField: boolean,
		currentOperator: ThemisConditionOperator,
		saveForOperator: (op: ThemisConditionOperator) => void,
	) => {
		if (noValueOperators.has(currentOperator)) {
			return <InlineHint>No value required for {currentOperator}.</InlineHint>;
		}

		if (listOperators.has(currentOperator) && isEnumField) {
			return (
				<SelectField
					multiple
					style={{ minHeight: "8rem" }}
					value={draft.enumListValues ?? []}
					onChange={(e) => {
						const picked = Array.from(e.target.selectedOptions).map(
							(o) => o.value,
						);
						setConditionDrafts((prev) => ({
							...prev,
							[c.id]: { ...prev[c.id], enumListValues: picked },
						}));
					}}
					onBlur={() => saveForOperator(currentOperator)}
				>
					{enumOptions.map((opt) => (
						<option
							key={`${c.id}_enum_list_${opt.value}`}
							value={String(opt.value)}
						>
							{opt.label}
						</option>
					))}
				</SelectField>
			);
		}

		if (listOperators.has(currentOperator) && !isEnumField) {
			const isStringList = vt === ThemisConditionValueType.String;
			return (
				<TextField
					name={
						isStringList
							? `cond_string_list_${c.id}`
							: `cond_number_list_${c.id}`
					}
					placeholder={isStringList ? "red, blue, green" : "10, 20, 30"}
					value={
						isStringList
							? (draft.stringListText ?? "")
							: (draft.numberListText ?? "")
					}
					onChange={(e) =>
						setConditionDrafts((prev) => ({
							...prev,
							[c.id]: {
								...prev[c.id],
								[isStringList ? "stringListText" : "numberListText"]:
									e.target.value,
							},
						}))
					}
					onBlur={() => saveForOperator(currentOperator)}
				/>
			);
		}

		if (betweenOperators.has(currentOperator)) {
			const isDecimalRange = vt === ThemisConditionValueType.Decimal;
			return (
				<div className="grid grid-cols-2 gap-[.5rem]">
					<TextField
						name={`${isDecimalRange ? "cond_decimal_start_" : "cond_long_start_"}${c.id}`}
						placeholder={
							vt === ThemisConditionValueType.DateTimeUtcMs
								? "Start UTC ms"
								: "Start"
						}
						value={
							isDecimalRange
								? (draft.decimalValue ?? "")
								: (draft.longValue ?? "")
						}
						onChange={(e) => {
							const nextValue = e.target.value;
							if (
								(isDecimalRange && !isDecimal(nextValue)) ||
								(!isDecimalRange && !isLong(nextValue))
							)
								return;
							setConditionDrafts((prev) => ({
								...prev,
								[c.id]: {
									...prev[c.id],
									[isDecimalRange ? "decimalValue" : "longValue"]: nextValue,
								},
							}));
						}}
						onBlur={() => saveForOperator(currentOperator)}
					/>
					<TextField
						name={`${isDecimalRange ? "cond_decimal_end_" : "cond_long_end_"}${c.id}`}
						placeholder={
							vt === ThemisConditionValueType.DateTimeUtcMs
								? "End UTC ms"
								: "End"
						}
						value={
							isDecimalRange
								? (draft.decimalValue2 ?? "")
								: (draft.longValue2 ?? "")
						}
						onChange={(e) => {
							const nextValue = e.target.value;
							if (
								(isDecimalRange && !isDecimal(nextValue)) ||
								(!isDecimalRange && !isLong(nextValue))
							)
								return;
							setConditionDrafts((prev) => ({
								...prev,
								[c.id]: {
									...prev[c.id],
									[isDecimalRange ? "decimalValue2" : "longValue2"]: nextValue,
								},
							}));
						}}
						onBlur={() => saveForOperator(currentOperator)}
					/>
				</div>
			);
		}

		if (vt === ThemisConditionValueType.Boolean) {
			return (
				<SelectField
					value={(draft.boolValue ?? false) ? "true" : "false"}
					onChange={(e) =>
						setConditionDrafts((prev) => ({
							...prev,
							[c.id]: { ...prev[c.id], boolValue: e.target.value === "true" },
						}))
					}
					onBlur={() => saveForOperator(currentOperator)}
				>
					<option value="true">True</option>
					<option value="false">False</option>
				</SelectField>
			);
		}

		if (vt === ThemisConditionValueType.Int && isEnumField) {
			return (
				<SelectField
					value={
						draft.intValue ??
						(c.intValue != null
							? String(c.intValue)
							: c.longValue != null
								? String(c.longValue)
								: "")
					}
					onChange={(e) =>
						setConditionDrafts((prev) => ({
							...prev,
							[c.id]: { ...prev[c.id], intValue: e.target.value },
						}))
					}
					onBlur={() => saveForOperator(currentOperator)}
				>
					{enumOptions.map((opt) => (
						<option key={`${c.id}_enum_${opt.value}`} value={String(opt.value)}>
							{opt.label}
						</option>
					))}
				</SelectField>
			);
		}

		const isStringValue = vt === ThemisConditionValueType.String;
		const isIntValue = vt === ThemisConditionValueType.Int;
		const isDecimalValue = vt === ThemisConditionValueType.Decimal;
		const fieldName = isStringValue
			? "stringValue"
			: isIntValue
				? "intValue"
				: isDecimalValue
					? "decimalValue"
					: "longValue";
		const nextValue = draft[fieldName] ?? "";
		const placeholder = isStringValue
			? "Text value"
			: isDecimalValue
				? "0.00"
				: vt === ThemisConditionValueType.DateTimeUtcMs
					? "UTC ms timestamp"
					: "0";

		return (
			<TextField
				name={`cond_${fieldName}_${c.id}`}
				placeholder={placeholder}
				value={nextValue}
				onChange={(e) => {
					const incomingValue = e.target.value;
					if (
						(isIntValue && !isInt(incomingValue)) ||
						(!isIntValue &&
							!isStringValue &&
							!isDecimalValue &&
							!isLong(incomingValue)) ||
						(isDecimalValue && !isDecimal(incomingValue))
					) {
						return;
					}
					setConditionDrafts((prev) => ({
						...prev,
						[c.id]: { ...prev[c.id], [fieldName]: incomingValue },
					}));
				}}
				onBlur={() => saveForOperator(currentOperator)}
			/>
		);
	};

	return (
		<Wrap>
			<HeaderBar>
				<div>
					<PanelTitle>Conditions</PanelTitle>
					<Muted>
						Any group can match. Every condition inside a group must match for
						that group to pass.
					</Muted>
				</div>

				<CreatePanel>
					<div>
						<TextField
							name="newGroupName"
							placeholder="New condition group"
							value={newGroupName}
							onChange={(e) => setNewGroupName(e.target.value)}
						/>
					</div>

					<Button
						icon="plus"
						onClick={() => void createGroup()}
						disabled={!ruleId}
					>
						Add Group
					</Button>
				</CreatePanel>
			</HeaderBar>

			{sortedGroups.length === 0 ? (
				<SelectBox>No condition groups yet. Add one to start.</SelectBox>
			) : (
				<div className="flex flex-col gap-[1rem]">
					{sortedGroups.map((g, groupIndex) => {
						const sortedConditions = (g.conditions ?? [])
							.slice()
							.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

						return (
							<GroupCard key={g.id}>
								<GroupHeader>
									<GroupHeaderCopy>
										<Eyebrow>OR Group {groupIndex + 1}</Eyebrow>
										<TextField
											name={`groupName_${g.id}`}
											placeholder="Group name"
											value={groupNameDrafts[g.id] ?? g.name ?? ""}
											onChange={(e) => {
												const val = e.target.value;

												setGroupNameDrafts((prev) => ({
													...prev,
													[g.id]: val,
												}));

												debouncedSaveGroupName(g, val);
											}}
											onBlur={() => void saveGroupName(g)}
										/>
									</GroupHeaderCopy>

									<GroupHeaderActions>
										<Button
											className="button-light"
											icon="plus"
											onClick={() => void handleAddConditionClick(g)}
										>
											Add Condition
										</Button>

										<IconButton
											type="button"
											aria-label={`Delete group ${g.name || g.id}`}
											onClick={() => void removeGroup(g.id)}
										>
											<Icon
												icon="trash"
												color="currentColor"
												width="18px"
												height="18px"
											/>
										</IconButton>
									</GroupHeaderActions>
								</GroupHeader>

								<GroupBody>
									<GroupSummary>
										<MetaPill>AND logic inside group</MetaPill>
										<MetaPill>
											{sortedConditions.length}{" "}
											{sortedConditions.length === 1
												? "condition"
												: "conditions"}
										</MetaPill>
									</GroupSummary>
									{sortedConditions.length === 0 ? (
										<SelectBox>No conditions in this group yet</SelectBox>
									) : (
										sortedConditions.map((c) => {
											const meta = fieldMetaByKey[c.fieldKey];
											const vt = meta?.valueType ?? intToValueType(c.valueType);
											const enumOptions = meta?.enumOptions ?? [];
											const isEnumField = enumOptions.length > 0;
											const currentOperator = intToOperator(c.operator);
											const allowedOperators =
												getAllowedOperatorsForCondition(c);
											const draft = conditionDrafts[c.id] ?? {};
											const saveForOperator = (op: ThemisConditionOperator) =>
												void setConditionValue(c, buildValuePatch(c, op));

											return (
												<ConditionRow key={c.id}>
													<FieldStack>
														<FieldLabel>Field</FieldLabel>
														<ThemisFieldPickerButton
															value={c.fieldKey}
															active={activePickerConditionId === c.id}
															onClick={() => void pickFieldForCondition(c)}
														/>
													</FieldStack>

													<FieldStack>
														<FieldLabel>Operator</FieldLabel>
														<SelectField
															value={currentOperator}
															onChange={(e) => {
																const nextOp = e.target
																	.value as ThemisConditionOperator;
																void setConditionValue(c, {
																	operator: operatorToInt(nextOp),
																	...buildValuePatch(c, nextOp),
																});
															}}
														>
															{allowedOperators.map((op) => (
																<option key={`${c.id}_${op}`} value={op}>
																	{op}
																</option>
															))}
														</SelectField>
													</FieldStack>

													<FieldStack>
														<FieldLabel>
															{getValueLabel(currentOperator, vt, isEnumField)}
														</FieldLabel>
														{renderConditionValueEditor(
															c,
															draft,
															vt,
															enumOptions,
															isEnumField,
															currentOperator,
															saveForOperator,
														)}
													</FieldStack>

													<IconButton
														type="button"
														aria-label={`Delete condition ${c.id}`}
														onClick={() => void removeCondition(c.id)}
													>
														<Icon
															icon="trash"
															color="currentColor"
															width="18px"
															height="18px"
														/>
													</IconButton>
												</ConditionRow>
											);
										})
									)}
								</GroupBody>
							</GroupCard>
						);
					})}
				</div>
			)}
		</Wrap>
	);
}
